/**
 * Collaborator invitation email: subject + HTML body.
 *
 * WHY THIS MODULE EXISTS. The same prose lived in three separate functions —
 * `collaborators/invite.vue` (new invite, and resend) and `collaborators/nav.vue`
 * (resend) — byte-identical apart from which variable supplied the recipient's
 * name. Adding a Show Pass variant to each would have made six copies, and the
 * next copy change would have fixed some and missed the rest. Same failure shape
 * as the Stripe price ids living in three places.
 *
 * The email is composed CLIENT-side and sent via `lib/sendMail.js`, which calls
 * the authenticated `sendTransactionalEmail` callable. That function is a dumb
 * transport: it takes `email` / `subject` / `content` and sends them. So this
 * file is the whole of the invitation copy.
 */

/**
 * Escape text that will be interpolated into the HTML body.
 *
 * Project titles and display names are user-supplied and were previously
 * interpolated raw, so a project called `Cats & Dogs` or `<Untitled>` produced a
 * malformed email. Cheap to fix while the copy is being moved.
 */
function esc(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** What Cuelist is. Identical in both variants, so it is written once. */
function whatCuelistIs() {
    return `<p>Cuelist is web-based software designed to help you and your collaborators create,
                edit and share cues and notes directly on a PDF script in real time. Your scripts
                stay up to date and are permanently archived on the Cuelist secure servers, and you
                can export a PDF at any time.</p>`;
}

/** Who to ask. Identical in both variants. */
function whoToAsk(inviterName, inviterEmail) {
    const n = esc(inviterName);
    const e = esc(inviterEmail);
    return `<p>If you have any questions about the invitation you can reach out to <strong>${n}</strong>
                at <a href="mailto:${e}">${e}</a>.<br />
                If you want to know more about Cuelist, you can email tech support at:
                <a href="mailto:support@thecuelist.com">support@thecuelist.com</a></p>
            <p>Thanks for using Cuelist!</p>`;
}

/**
 * @param {object} project        the project doc — `title`, `id`, and `unlocked`
 * @param {boolean} isShowPass    convenience override; defaults to project.unlocked
 */
export function inviteSubject(project, isShowPass) {
    const showPass = isShowPass === undefined ? !!(project && project.unlocked) : !!isShowPass;
    const rawTitle = project && project.title;
    // Quoted so the title cannot blur into the sentence: show names are
    // arbitrary text and plenty of them are ordinary words -- "Invitation to
    // collaborate on Company via Cuelist" is ambiguous in a way that
    // "Company" is not.
    //
    // ⚠ A REAL title only. A project with no title falls back to unquoted
    // "a Cuelist project"; quoting that reads as though the show is literally
    // called that.
    const title = rawTitle ? `“${rawTitle}”` : 'a Cuelist project';
    // Plain text: a subject line is not HTML and must not carry entities.
    //
    // Both variants lead with "Invitation to collaborate on" (Josh, 14 Sept
    // 2026). The Show Pass line used to read "You can edit X on Cuelist, no
    // subscription needed", which reads as marketing rather than an invitation
    // and never says it IS one -- confusing for a recipient who has never heard
    // of Cuelist, which is most of them, since a buyer's first act is inviting
    // their team.
    //
    return showPass
        ? `Invitation to collaborate on ${title} via Cuelist Show Pass`
        : `Invitation to collaborate on ${title} via Cuelist`;
}

/**
 * @param {object} opts.project        the project doc (needs `title`, `id`, `unlocked`)
 * @param {string} opts.recipientName  who the email greets
 * @param {string} opts.inviterName    the inviting user's display name
 * @param {string} opts.inviterEmail   the inviting user's email
 * @param {string} opts.host           the app host, for the accept link
 * @param {boolean} [opts.isShowPass]  override; defaults to project.unlocked
 */
export function inviteBody({ project, recipientName, inviterName, inviterEmail, host, isShowPass }) {
    const p = project || {};
    const showPass = isShowPass === undefined ? !!p.unlocked : !!isShowPass;
    const inviteLink = `https://${host || 'app.thecuelist.com'}/invite/${encodeURIComponent(p.id || '')}`;
    const who = esc(recipientName);
    const inviter = esc(inviterName);
    const title = esc(p.title);

    if (showPass) {
        // A Show Pass covers every collaborator on that ONE project, permanently and
        // at no cost to them. The standard copy invites them to "try Cuelist for
        // FREE", which describes a trial: a clock that runs out and then asks for
        // money. That is not what they have been given, and it undersells the thing
        // the buyer paid for.
        //
        // "no billing information" is a promise, and it is true: register-form.vue
        // collects first name, last name, email and password only, and login.vue sets
        // stripe_id to empty on create. If signup ever starts asking for a card, this
        // sentence has to change with it.
        //
        // No price appears here — the pricing page is the single source for that, and
        // the invitee is not the buyer in any case.
        return `<p>Hi ${who},</p>
            <p>You have been invited by <strong>${inviter}</strong> to collaborate on the Cuelist
               project <strong>${title}</strong>.</p>
            <p><strong>This production is covered by a Show Pass</strong>, so full editing is
               included for you on this project at no cost. You do not need a Cuelist subscription
               of your own and you will not need to provide any billing information in order to
               activate the project.</p>
            ${whatCuelistIs()}
            <p>To get started: if you already have a Cuelist account, click
               <a href="${inviteLink}">here</a> to accept and open the project.<br />
               If you are new to Cuelist, you will need a free account first. Sign up at
               <a href="https://thecuelist.com">thecuelist.com</a>, then use the same link to join.</p>
            ${whoToAsk(inviterName, inviterEmail)}`;
    }

    return `<p>Hi ${who},</p>
        <p>You have been invited by <strong>${inviter}</strong> to collaborate on the Cuelist
           project <strong>${title}</strong>.</p>
        ${whatCuelistIs()}
        <p>If you are a current user of Cuelist, click <a href="${inviteLink}">here</a> to accept
           your invite to this project.<br />
           If you are new to Cuelist, click <a href="https://thecuelist.com">here</a> to learn more
           and sign up to try Cuelist today for FREE.</p>
        ${whoToAsk(inviterName, inviterEmail)}`;
}

export default { inviteSubject, inviteBody };


// WEBPACK FOOTER //
// ./src/lib/inviteEmail.js